import React, { useState, useEffect, useRef, useCallback } from 'react';
import { diariesApi, resolveApiUrl } from '../api';

interface Diary {
  id: number;
  person_id: number;
  content: string;
  date: string;
  created_at: string;
  images?: DiaryImage[];
}

interface DiaryImage {
  id: number;
  url: string;
  created_at?: string;
}

interface DiaryFormProps {
  personId: number;
  diary?: Diary | null;
  onClose: () => void;
  onSubmit: () => void;
}

const DiaryForm: React.FC<DiaryFormProps> = ({ personId, diary, onClose, onSubmit }) => {
  const [content, setContent] = useState('');
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const draftKey = `diary_draft_${personId}_${diary?.id || 'new'}`;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputGalleryRef = useRef<HTMLInputElement>(null);

  const closePreview = () => {
    if (previewImageUrl && previewImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewImageUrl);
    }
    setPreviewImageUrl(null);
  };

  // 计算字数
  const calculateWordCount = (text: string) => {
    return text.trim().length;
  };

  // 保存草稿到本地存储
  const saveDraft = useCallback((content: string, date: string) => {
    try {
      localStorage.setItem(draftKey, JSON.stringify({ content, date, timestamp: Date.now() }));
    } catch (error) {
      console.error('保存草稿失败:', error);
    }
  }, [draftKey]);

  // 加载草稿
  const loadDraft = useCallback(() => {
    try {
      const draft = localStorage.getItem(draftKey);
      if (draft) {
        const { content: draftContent, date: draftDate } = JSON.parse(draft);
        if (draftContent && draftContent.trim()) {
          return { content: draftContent, date: draftDate || '' };
        }
      }
    } catch (error) {
      console.error('加载草稿失败:', error);
    }
    return null;
  }, [draftKey]);

  // 清除草稿
  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(draftKey);
    } catch (error) {
      console.error('清除草稿失败:', error);
    }
  }, [draftKey]);

  // 初始化数据
  useEffect(() => {
    if (diary) {
      setContent(diary.content);
      if (diary.date) {
        const dateObj = new Date(diary.date);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        setDate(`${year}-${month}-${day}T${hours}:${minutes}`);
      } else {
        setDate('');
      }
    } else {
      const draft = loadDraft();
      if (draft) {
        setContent(draft.content);
        setDate(draft.date || '');
        setHasUnsavedChanges(true);
      } else {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        setDate(`${year}-${month}-${day}T${hours}:${minutes}`);
      }
    }
    setSelectedFiles([]);
  }, [diary, loadDraft]);

  const addFiles = (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    const list = Array.isArray(files) ? files : Array.from(files);
    if (list.length === 0) return;
    setSelectedFiles(prev => {
      const merged = [...prev, ...list];
      const uniq: File[] = [];
      const seen = new Set<string>();
      for (const f of merged) {
        const key = `${f.name}_${f.size}_${f.lastModified}`;
        if (!seen.has(key)) { seen.add(key); uniq.push(f); }
      }
      if (uniq.length > 9) {
        alert('最多只能选择 9 张图片');
        return uniq.slice(0, 9);
      }
      return uniq;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files;
    if (raw && raw.length > 0) {
      addFiles(Array.from(raw));
    }
    e.target.value = ''; // 允许再次选同一文件
  };

  const triggerCameraInput = () => {
    if (selectedFiles.length >= 9 || loading) return;
    fileInputRef.current?.click();
  };
  const triggerGalleryInput = () => {
    if (selectedFiles.length >= 9 || loading) return;
    fileInputGalleryRef.current?.click();
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
  const handleSubmitRef = useRef<((event: React.FormEvent) => Promise<void>) | null>(null);

  // 更新字数统计
  useEffect(() => {
    setWordCount(calculateWordCount(content));
  }, [content]);

  // 自动保存草稿（防抖）
  useEffect(() => {
    if (!diary && (content.trim() || date)) {
      setHasUnsavedChanges(true);
      const timer = setTimeout(() => {
        saveDraft(content, date);
      }, 1000); // 1秒后保存草稿

      return () => clearTimeout(timer);
    }
  }, [content, date, diary, saveDraft]);

  // 快捷键支持
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S 或 Cmd+S 保存
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (content.trim() && !loading) {
          void handleSubmitRef.current?.(e as any);
        }
      }
      // Esc 关闭
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [content, loading, onClose]);

  // 自动聚焦到文本区域
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      // 将光标移到末尾
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) {
      alert('请输入日记内容');
      return;
    }

    setLoading(true);
    try {
      const dateValue = date ? new Date(date).toISOString() : undefined;
      
      if (diary) {
        await diariesApi.update(diary.id, content, dateValue);
        if (selectedFiles.length > 0) {
          await diariesApi.uploadImages(diary.id, selectedFiles);
        }
      } else {
        const created = await diariesApi.create({ person_id: personId, content, date: dateValue });
        const diaryId = created?.data?.id;
        if (diaryId && selectedFiles.length > 0) {
          await diariesApi.uploadImages(diaryId, selectedFiles);
        }
      }
      
      // 清除草稿
      clearDraft();
      setHasUnsavedChanges(false);
      setSelectedFiles([]);
      onSubmit();
    } catch (error: any) {
      alert('操作失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };
  handleSubmitRef.current = handleSubmit;

  const handleClose = () => {
    if (hasUnsavedChanges && !diary) {
      if (window.confirm('有未保存的草稿，确定要关闭吗？')) {
        clearDraft();
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <div className="modal" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="modal-content diary-editor-modal">
        <div className="modal-header">
          <h2>{diary ? '编辑日记' : '写新日记'}</h2>
          <button className="close-btn" onClick={handleClose} title="关闭 (Esc)">×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>日期时间</label>
            <input
              type="datetime-local"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
              选择日记记录的日期和时间（默认为当前时间）
            </small>
          </div>
          
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
              <label>日记内容 *</label>
              <span style={{ 
                fontSize: '12px', 
                color: wordCount > 0 ? '#666' : '#999',
                fontWeight: 'normal'
              }}>
                字数: {wordCount}
              </span>
            </div>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              placeholder="记录关于这个人的性格特征、行为、事迹等...&#10;&#10;提示：&#10;- 详细描述这个人的行为表现&#10;- 记录性格特征和习惯&#10;- 记录互动经历和感受&#10;- AI会根据这些信息生成性格分析"
              rows={15}
              style={{
                fontFamily: 'inherit',
                lineHeight: '1.6',
                fontSize: '14px',
                resize: 'vertical',
                minHeight: '300px'
              }}
            />
            <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
              💡 提示：详细记录这个人的行为、性格特征、互动经历等，AI会根据这些信息生成性格分析。
              {hasUnsavedChanges && !diary && <span style={{ color: '#007bff', marginLeft: '10px' }}>💾 草稿已自动保存</span>}
            </small>
          </div>

          {/* 图片上传：隐藏 input + 按钮触发 click，保证桌面/移动端都能触发 onChange */}
          <div className="form-group">
            <label>图片（最多 9 张）</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
              onChange={handleFileChange}
              aria-hidden
            />
            <input
              ref={fileInputGalleryRef}
              type="file"
              accept="image/*"
              multiple
              style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
              onChange={handleFileChange}
              aria-hidden
            />
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={selectedFiles.length >= 9 || loading}
                onClick={triggerCameraInput}
                style={{ opacity: (selectedFiles.length >= 9 || loading) ? 0.6 : 1 }}
              >
                📷 拍照
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={selectedFiles.length >= 9 || loading}
                onClick={triggerGalleryInput}
                style={{ opacity: (selectedFiles.length >= 9 || loading) ? 0.6 : 1 }}
              >
                🖼️ 选图片
              </button>
              <span style={{ fontSize: 12, color: '#666' }}>{selectedFiles.length}/9</span>
            </div>

            {selectedFiles.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 10 }}>
                {selectedFiles.map((f, idx) => {
                  const url = URL.createObjectURL(f);
                  return (
                    <div key={`${f.name}_${f.size}_${f.lastModified}`} style={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={f.name}
                        role="button"
                        tabIndex={0}
                        style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 10, border: '1px solid #eee', cursor: 'pointer', display: 'block' }}
                        onLoad={() => URL.revokeObjectURL(url)}
                        onClick={() => setPreviewImageUrl(URL.createObjectURL(f))}
                      />
                      <button
                        type="button"
                        onClick={() => removeSelectedFile(idx)}
                        title="移除"
                        style={{
                          position: 'absolute', top: 6, right: 6,
                          width: 22, height: 22,
                          borderRadius: 999,
                          border: 'none',
                          background: 'rgba(0,0,0,0.55)',
                          color: 'white',
                          cursor: 'pointer',
                          lineHeight: '22px',
                          fontSize: 14,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 编辑模式：展示已上传图片（只读预览） */}
            {diary && Array.isArray(diary.images) && diary.images.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <small style={{ color: '#666', display: 'block', marginBottom: 6 }}>已上传图片</small>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  {diary.images.slice(0, 9).map(img => {
                    const url = resolveApiUrl(img.url);
                    return (
                      <button
                        key={img.id}
                        type="button"
                        onClick={() => setPreviewImageUrl(url)}
                        style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', borderRadius: 10, overflow: 'hidden' }}
                      >
                        <img
                          src={url}
                          alt="uploaded"
                          style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 10, border: '1px solid #eee', display: 'block' }}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {previewImageUrl && (
              <div
                role="dialog"
                aria-modal="true"
                aria-label="图片预览"
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 10000,
                  background: 'rgba(0,0,0,0.88)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 20,
                }}
                onClick={closePreview}
              >
                <img
                  src={previewImageUrl}
                  alt="预览"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </div>

          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            justifyContent: 'flex-end',
            paddingTop: '10px',
            borderTop: '1px solid #eee',
            marginTop: '20px'
          }}>
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              取消
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading || !content.trim()}>
              {loading ? '保存中...' : '保存'}
              <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.8 }}>(Ctrl+S)</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DiaryForm;


